import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Check, House, KeyRound, MapPin, RefreshCw, TriangleAlert } from 'lucide-react';
import {
  useAnnounceArrivalMutation,
  useGetMyChildrenQuery,
  useRevokeMyCodeMutation,
  getApiError,
  type ChildCode,
} from '@/store/api';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Mark } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const dateFmt = new Intl.DateTimeFormat('en-GH', { weekday: 'short', day: 'numeric', month: 'short' });

/** 448328 → "448 328". Six digits in one run get miscounted when read aloud. */
function spaced(code: string) {
  return code.replace(/(\d{3})(\d{3})/, '$1 $2');
}

/*
  ─────────────────────────────────────────────────────────────────────────────
  Today's pickup codes — the screen this whole product exists to show.

  A parent opens it in a car park and either reads six digits down a phone line
  or holds the QR up at a gate. So the code is the largest thing on the page by
  a wide margin, it is grouped 3+3 because that is how people read it aloud, and
  nothing decorative competes with it.

  Every other state is explicit rather than implied: released, revoked and
  marked-absent each say what they mean and what to do next, because a parent
  staring at a code that has already been used needs to know that is why the
  gate turned them away.
  ─────────────────────────────────────────────────────────────────────────────
*/
export function CodesPage() {
  const { data: children = [], isLoading, isError, error, isFetching, refetch } = useGetMyChildrenQuery();
  const [revoke, revokeState] = useRevokeMyCodeMutation();
  const [pendingRevoke, setPendingRevoke] = useState<ChildCode | null>(null);

  const doRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revoke(pendingRevoke.studentId).unwrap();
      setPendingRevoke(null);
    } catch {
      /* Kept open so the failure shows next to the thing that failed. */
    }
  };

  if (isLoading) return <PageLoader label="Loading your codes…" />;

  if (isError) {
    return (
      <div className={styles.panel}>
        <EmptyState
          icon={<TriangleAlert size={17} />}
          title="Could not load your codes"
          description={getApiError(error)}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className={styles.pageTitle}>Today's codes</h1>
          <p className={cn(styles.meta, 'mt-0.5')}>
            Show the QR code or read the digits at the gate.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Refresh"
          icon={<RefreshCw size={14} className={isFetching ? 'animate-spin' : undefined} />}
          onClick={() => void refetch()}
        />
      </div>

      {children.length === 0 && (
        <div className={styles.panel}>
          <EmptyState
            icon={<KeyRound size={17} />}
            title="No children linked to this number"
            description="Ask the school office to add this phone number to your child's record. Codes appear here once it is linked."
          />
        </div>
      )}

      {children.map((child) => {
        const firstName = child.studentName.split(' ')[0];

        return (
          <div key={child.studentId} className={cn(styles.panel, 'overflow-clip')}>
            {/* ── Child ─────────────────────────────────────────────────── */}
            <div className="flex items-center gap-3 border-b border-border px-4 py-3">
              <Avatar src={child.photoUrl} name={child.studentName} size={40} zoomable caption={child.studentClass} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-bold text-foreground">{child.studentName}</p>
                <p className="truncate text-[11px] text-muted-foreground">{child.studentClass}</p>
              </div>
              {child.isUsed && <Mark tone="success">Released</Mark>}
              {child.isRevoked && <Mark tone="danger">Revoked</Mark>}
              {child.isAbsent && <Mark tone="warning">Absent</Mark>}
            </div>

            {/* ── Absent ────────────────────────────────────────────────── */}
            {child.isAbsent ? (
              <div className="flex items-start gap-2.5 px-4 py-4">
                <House size={15} className="mt-[2px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
                <div>
                  <p className="text-[12.5px] font-bold text-foreground">Not at school today</p>
                  <p className={cn(styles.meta, 'mt-0.5')}>
                    {firstName} was marked absent by the school, so no pickup code was issued. If this
                    is wrong, call the school office.
                  </p>
                </div>
              </div>
            ) : child.isRevoked ? (
              <div className="flex items-start gap-2.5 px-4 py-4">
                <TriangleAlert size={15} className="mt-[2px] flex-shrink-0 text-[hsl(var(--record-ink-danger))]" />
                <div>
                  <p className="text-[12.5px] font-bold text-foreground">This code was cancelled</p>
                  <p className={cn(styles.meta, 'mt-0.5')}>
                    A replacement has been sent to you by SMS. Pull to refresh once it arrives.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* ── The code ─────────────────────────────────────────── */}
                <div className="px-4 pt-4 text-center">
                  <p className={styles.label}>Pickup code</p>
                  <p
                    className="mt-1.5 text-[38px] font-extrabold leading-none tabular-nums text-foreground"
                    style={{ letterSpacing: '0.12em' }}
                    /* Read aloud far more often than it is read silently. */
                    aria-label={`Pickup code ${child.code.split('').join(' ')}`}
                  >
                    {spaced(child.code)}
                  </p>
                </div>

                {/* ── QR ───────────────────────────────────────────────────
                    White ground and near-black modules regardless of theme:
                    a scanner needs the contrast, and an inverted QR on a dark
                    card does not read on many gate devices. */}
                <div className="flex justify-center px-4 pt-4">
                  <div className="rounded-md border border-border bg-white p-2.5">
                    <QRCodeSVG
                      value={`safepass://verify?code=${child.code}&studentId=${child.studentId}`}
                      size={148}
                      level="M"
                      bgColor="#FFFFFF"
                      fgColor="#101B29"
                    />
                  </div>
                </div>

                <p className="px-4 pt-3 text-center text-[10.5px] text-muted-foreground">
                  Expires at midnight · {dateFmt.format(new Date(child.expiresAt))}
                </p>

                {child.isUsed ? (
                  <div className="mt-3 border-t border-border px-4 py-3">
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      {firstName} has already been released today. This code cannot be used again.
                    </p>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-col gap-2 border-t border-border px-4 py-3">
                    <AnnounceButton child={child} firstName={firstName} />
                    <Button
                      variant="danger"
                      size="md"
                      className="w-full"
                      loading={revokeState.isLoading && revokeState.originalArgs === child.studentId}
                      onClick={() => setPendingRevoke(child)}
                    >
                      Cancel this code &amp; get a new one
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      <ConfirmDialog
        open={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        onConfirm={() => void doRevoke()}
        title="Cancel this code?"
        confirmLabel="Cancel & regenerate"
        loading={revokeState.isLoading}
        body={
          <>
            {pendingRevoke?.studentName.split(' ')[0]}'s current code stops working immediately, and a
            new one is sent to you by SMS. Do this if someone else may have seen or been given the
            code.
            {revokeState.error && (
              <p className="mt-2 font-medium text-[hsl(var(--record-ink-danger))]">
                {getApiError(revokeState.error)}
              </p>
            )}
          </>
        }
      />
    </div>
  );
}

/*
  "I'm here."

  The one tap that makes dismissal quick. Pressed from the car park it puts the
  child on the school's board, so the walk from the classroom happens WHILE the
  parent queues instead of after they reach the window — which is the whole
  difference between a three-minute wait and a thirty-minute one.

  Announcing deliberately needs no code. It only asks the school to send the
  child down; the code is still required at the gate before anybody is handed
  over, so the low-stakes half moves early and the high-stakes half does not
  move at all.
*/
function AnnounceButton({ child, firstName }: { child: ChildCode; firstName: string }) {
  const [announce, { isLoading, isSuccess, error }] = useAnnounceArrivalMutation();

  if (isSuccess) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-success/30 bg-success/[0.08] px-3 py-2.5">
        <Check size={14} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-success))]" />
        <p className="text-[11.5px] leading-relaxed text-[hsl(var(--record-ink-success))]">
          The school knows you are here. {firstName} is on their way to the gate — have your code
          ready.
        </p>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="primary"
        size="lg"
        className="w-full"
        icon={<MapPin size={15} />}
        loading={isLoading}
        onClick={() => void announce({ studentId: child.studentId })}
      >
        I'm here — send {firstName} down
      </Button>
      {error && (
        <p className="text-[11px] text-[hsl(var(--record-ink-danger))]">{getApiError(error)}</p>
      )}
      <p className="text-center text-[10.5px] leading-relaxed text-muted-foreground">
        Tap this as you arrive. You will still show the code at the gate.
      </p>
    </>
  );
}
